import { NotificationRuleNotificationType } from '@eppendorf/vnls-notifications-utils';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  Icon,
  IconSizeClasses,
} from '@eppendorf/vnls-react-components';
import { useCallback, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import './lifecycle-screen.scss';
import { AddNotificationRuleScreenProps } from '$features/notifications/types';

type LifecycleNotificationType = Exclude<NotificationRuleNotificationType, 'Escalated'>;
const alwaysEnabledNotificationType: LifecycleNotificationType = 'Created';

const selectableNotificationTypes: LifecycleNotificationType[] = [
  'Acknowledged',
  'AutoResolved',
  'Expired',
  'Archived',
];

interface NotificationTypeListItemProps {
  type: LifecycleNotificationType;
  checked: boolean;
  disabled?: boolean;
  onSelect: (type: LifecycleNotificationType) => void;
}

function NotificationTypeListItem({
  type,
  checked,
  disabled,
  onSelect,
}: NotificationTypeListItemProps) {
  const { t } = useTranslation();

  return (
    <div
      className={`notification-lifecycle-screen__item p-l flex flex__ai--center gap-m${
        disabled ? '' : ' cursor--pointer'
      }${
        // eslint-disable-next-line no-nested-ternary -- readable given the 3 mutually exclusive states
        disabled
          ? ' notification-lifecycle-screen__item--disabled'
          : checked
            ? ' notification-lifecycle-screen__item--selected'
            : ''
      }`}
    >
      <Checkbox
        checked={checked}
        disabled={disabled}
        onChange={() => onSelect(type)}
        ariaLabel={type}
      />
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- clickable area */}
      <div
        className="flex flex__dir--column gap-xs"
        onClick={() => !disabled && onSelect(type)}
        onKeyDown={() => !disabled && onSelect(type)}
      >
        <div className="flex flex__ai--center gap-s">
          <strong className="font-weight-bold">
            {t(`notificationRules.notificationTypes.${type}` as const)}
          </strong>
          {disabled && (
            <Badge size="small" color="blue">
              {t('notificationRules.notificationTypesAlwaysEnabled')}
            </Badge>
          )}
        </div>
        <span className="color-gray-700">
          {t(`notificationRules.notificationTypesDescriptions.${type}` as const)}
        </span>
      </div>
    </div>
  );
}

interface LifecycleScreenProps extends AddNotificationRuleScreenProps {
  onBack: () => void;
  onContinue: () => void;
  screenHasErrors: boolean;
}

export function LifecycleScreen({
  formControl,
  onBack,
  onContinue,
  screenHasErrors,
}: LifecycleScreenProps) {
  const { t } = useTranslation();
  const { watch, setValue, register } = formControl;

  useEffect(() => {
    register('notificationTypes', {
      validate: (notificationTypes) => notificationTypes?.length > 0,
    });
    if (!watch('notificationTypes')?.length) {
      setValue('notificationTypes', [alwaysEnabledNotificationType]);
    }
    formControl.trigger('notificationTypes');
  }, [register]);

  const handleNotificationTypeSelect = useCallback(
    (type: LifecycleNotificationType) => {
      const notificationTypes = watch('notificationTypes') || [];
      if (notificationTypes.includes(type)) {
        setValue(
          'notificationTypes',
          notificationTypes.filter((selectedType) => selectedType !== type),
          { shouldDirty: true },
        );
      } else {
        setValue('notificationTypes', [...notificationTypes, type], {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    },
    [watch, setValue],
  );

  const selectedNotificationTypes = watch('notificationTypes');
  const allNotificationTypesSelected = useMemo(
    () =>
      selectableNotificationTypes.every((type) =>
        selectedNotificationTypes?.includes(type),
      ),
    [selectedNotificationTypes],
  );

  return (
    <>
      <p className="p-bottom-xxl">{t('notificationRules.lifecycleScreen.hint')}</p>

      <div className="flex flex__dir--column gap-l">
        <NotificationTypeListItem
          type={alwaysEnabledNotificationType}
          checked
          disabled
          onSelect={() => {}}
        />
        {selectableNotificationTypes.map((type) => (
          <NotificationTypeListItem
            key={type}
            type={type}
            checked={watch('notificationTypes')?.includes(type)}
            onSelect={handleNotificationTypeSelect}
          />
        ))}
      </div>

      {allNotificationTypesSelected && (
        <div
          className="flex flex__dir--row flex__ai--center gap-m p-l m-top-l border-solid border-color-orange-500 bg-orange-50 border-radius-l"
          style={{ borderWidth: '1px' }}
        >
          <Icon name="warning" size={IconSizeClasses.XSmall} className="bg-orange-500" />
          <p className="font-size-m">
            <Trans
              i18nKey="notificationRules.lifecycleScreen.highVolumeWarning"
              components={{ bold: <strong className="font-weight-bold" /> }}
            />
          </p>
        </div>
      )}

      <Dialog.Actions>
        <Button variant="secondary" onClick={onBack}>
          {t('shared.back')}
        </Button>

        <Button onClick={onContinue} disabled={screenHasErrors}>
          {t('shared.continue')}
        </Button>
      </Dialog.Actions>
    </>
  );
}
